import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createPulseCard,
  getPulseErrorMessage,
  getPulsePayload,
  getPulseUploadedImageUrl,
  isPulseApiSuccess,
  uploadPulseImage,
} from '../../services/pulseApi';
import {
  HERO_PRESENTATIONS,
  datetimeLocalToIso,
  emptyKnowledgeForm,
  emptyOption,
  ensureOptionIds,
  normalizeHeroPresentation,
  parseCardPayload,
} from './pulseUtils';

function PulseKnowledgeUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(emptyKnowledgeForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const result = await uploadPulseImage(file);
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to upload image.'));
        return;
      }
      const url = getPulseUploadedImageUrl(result);
      if (!url) {
        setError('Upload finished but image URL was missing.');
        return;
      }
      updateField('image_url', url);
      setSnackbar({ open: true, message: 'Image uploaded. Create the card to persist image_url.' });
    } catch (uploadError) {
      setError(uploadError.message || 'Failed to upload image.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updateOption = (index, field, value) => {
    setForm((prev) => {
      const options = [...prev.options];
      options[index] = { ...options[index], [field]: value };
      return { ...prev, options };
    });
  };

  const addOption = () => {
    setForm((prev) => {
      if (prev.options.length >= 6) return prev;
      return { ...prev, options: [...prev.options, emptyOption()] };
    });
  };

  const removeOption = (index) => {
    setForm((prev) => {
      if (prev.options.length <= 2) return prev;
      const removed = prev.options[index];
      const options = prev.options.filter((_, i) => i !== index);
      const correct_option_id =
        prev.correct_option_id && prev.correct_option_id === removed.id
          ? ''
          : prev.correct_option_id;
      return { ...prev, options, correct_option_id };
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    const labeled = form.options.filter((o) => o.label.trim());
    if (labeled.length < 2 || labeled.length > 6) {
      setError('Daily knowledge needs 2–6 options.');
      return;
    }
    const matchIdRaw = String(form.match_id || '').trim();
    if (matchIdRaw && !/^\d+$/.test(matchIdRaw)) {
      setError('Match ID must be a numeric fixture id, or leave it empty.');
      return;
    }
    const options = ensureOptionIds(labeled);
    if (!form.correct_option_id && options.length) {
      setError('Pick the correct option. It is stored for grading and never sent on the app deck.');
      return;
    }
    const correct = options.find(
      (o) => o.id === form.correct_option_id || o.label === form.correct_option_id
    );
    if (!correct) {
      setError('correct_option_id must match one of the options.');
      return;
    }

    const payload = {
      title: form.title.trim(),
      summary: form.summary.trim() || null,
      options,
      correct_option_id: correct.id,
      reward_fe: Number(form.reward_fe) || 0,
      entry_fee_fe: Number(form.entry_fee_fe) || 0,
      status: form.status || 'active',
    };
    if (form.image_url.trim()) payload.image_url = form.image_url.trim();
    if (form.hero_presentation) {
      payload.hero_presentation = normalizeHeroPresentation(form.hero_presentation);
    }
    if (form.hero_aspect_ratio.trim()) {
      payload.hero_aspect_ratio = form.hero_aspect_ratio.trim();
    }
    const starts = datetimeLocalToIso(form.starts_at);
    const deadline = datetimeLocalToIso(form.deadline_at);
    const expires = datetimeLocalToIso(form.expires_at);
    if (starts) payload.starts_at = starts;
    if (deadline) payload.deadline_at = deadline;
    if (expires) payload.expires_at = expires;
    if (matchIdRaw) payload.match_id = Number(matchIdRaw);

    setSaving(true);
    setError('');
    try {
      const result = await createPulseCard(payload);
      if (!isPulseApiSuccess(result)) {
        setError(getPulseErrorMessage(result, 'Failed to create knowledge card.'));
        return;
      }
      const created = parseCardPayload(getPulsePayload(result));
      const savedId = created?.id;
      setSnackbar({ open: true, message: 'Knowledge card created. Correct answer is admin-only.' });
      setTimeout(() => {
        navigate(savedId ? `/admin/pulse/cards/${savedId}` : '/admin/pulse');
      }, 600);
    } catch (saveError) {
      setError(saveError.message || 'Failed to create knowledge card.');
    } finally {
      setSaving(false);
    }
  };

  const optionIds = ensureOptionIds(form.options.map((o) => ({ ...o, label: o.label || o.id })));

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Daily knowledge upload
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        2–6 options and a correct answer. Default window is 24h from start if you leave times empty. The
        correct option is stored for instant grading (correct → awarded, wrong → forfeited, 0 FE) and is
        never sent on the mobile GET wire. Optional <code>match_id</code> sets the app match pill to that
        fixture’s short names (for example IND vs WI). Leave it empty and if the title names two sides
        (“Most odi wickets for IND vs WI?”), the pill is just those names — the pill is not the question.
        Optional hero presentation (<code>bleed</code> / <code>backdrop</code>) and aspect ratio go on the
        wire; omit → app bleed. Backdrop heroes use <code>surface</code> wash tint with default{' '}
        <code>wash_opacity</code> 0.82 for readable overlaid text. Option order is shuffled on create so
        the correct answer is not always first.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                value={form.status}
                onChange={(e) => updateField('status', e.target.value)}
                margin="dense"
              >
                <MenuItem value="active">Active (publish)</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Summary"
                value={form.summary}
                onChange={(e) => updateField('summary', e.target.value)}
                margin="dense"
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Match ID (optional)"
                value={form.match_id}
                onChange={(e) => updateField('match_id', e.target.value)}
                margin="dense"
                placeholder="91557"
                helperText="Sets the app pill to that match’s short names. Empty → pill parsed from the title when it names two sides."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL (optional)"
                value={form.image_url}
                onChange={(e) => updateField('image_url', e.target.value)}
                margin="dense"
                helperText="HTTPS CDN URL (Cloudflare when enabled), or upload below. Empty → no photo on trivia/predictions"
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : 'Upload'}
                  <input
                    ref={fileInputRef}
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleUploadImage}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  POST /admin/pulse/upload-image → CDN image_url → then create card
                </Typography>
              </Box>
              {form.image_url ? (
                <Box
                  component="img"
                  src={form.image_url}
                  alt=""
                  sx={{ mt: 1, maxHeight: 120, maxWidth: 240, objectFit: 'cover', borderRadius: 1 }}
                />
              ) : null}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Hero presentation"
                value={form.hero_presentation}
                onChange={(e) => updateField('hero_presentation', e.target.value)}
                margin="dense"
                helperText="Omit / unknown → app bleed. Backdrop: surface wash + wash_opacity 0.82 (app default)"
              >
                {HERO_PRESENTATIONS.map((p) => (
                  <MenuItem key={p.value || 'default'} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hero aspect ratio"
                value={form.hero_aspect_ratio}
                onChange={(e) => updateField('hero_aspect_ratio', e.target.value)}
                margin="dense"
                placeholder="4:3"
                helperText='Optional W:H e.g. "4:3"'
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Reward FE"
                value={form.reward_fe}
                onChange={(e) => updateField('reward_fe', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Entry fee FE"
                value={form.entry_fee_fe}
                onChange={(e) => updateField('entry_fee_fe', e.target.value)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Starts at"
                value={form.starts_at}
                onChange={(e) => updateField('starts_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty for now; window defaults to +24h"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Deadline at"
                value={form.deadline_at}
                onChange={(e) => updateField('deadline_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Expires at"
                value={form.expires_at}
                onChange={(e) => updateField('expires_at', e.target.value)}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Options (2–6) — select the correct answer
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addOption}
              size="small"
              disabled={form.options.length >= 6}
            >
              Add option
            </Button>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={56}>Correct</TableCell>
                <TableCell>ID (optional)</TableCell>
                <TableCell>Label</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.options.map((opt, index) => {
                const resolvedId = optionIds[index]?.id || '';
                return (
                  <TableRow key={index}>
                    <TableCell>
                      <Radio
                        checked={form.correct_option_id === opt.id || form.correct_option_id === resolvedId}
                        onChange={() => updateField('correct_option_id', opt.id || resolvedId)}
                        disabled={!opt.label.trim() && !opt.id.trim()}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={opt.id}
                        onChange={(e) => {
                          const nextId = e.target.value;
                          setForm((prev) => {
                            const options = [...prev.options];
                            options[index] = { ...options[index], id: nextId };
                            const correct_option_id =
                              prev.correct_option_id === opt.id ? nextId : prev.correct_option_id;
                            return { ...prev, options, correct_option_id };
                          });
                        }}
                        placeholder={resolvedId || 'auto from label'}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={opt.label}
                        onChange={(e) => updateOption(index, 'label', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeOption(index)}
                        disabled={form.options.length <= 2}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Creating…' : 'Create knowledge card'}
        </Button>
        <Button component={Link} to="/admin/pulse">
          Cancel
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default PulseKnowledgeUpload;
